<script setup lang="ts">
import { reactive, ref, nextTick, computed } from "vue";
import { useSetupAccount } from "../../store/resetPassword";
import MInput from "../../components/medicus/inputs/MInput";
import MButton from "../../components/medicus/MButton";
import MBanner from "../../components/medicus/MBanner";
import MForm from "../../components/medicus/MForm";
import MLayoutStack from "../../components/medicus/MLayoutStack";
import { useRouter } from "vue-router";
import { useAuth } from "../../store";

const props = defineProps<{ setupCode: string; username: string }>();

const router = useRouter();

const errorsUi = ref<string[] | []>([]);
const passwordMismatch = ref(false);
const otpCode = ref("");
const errorsServer = ref<string[]>([]);

const minPasswordLength = 8;

const store = useSetupAccount();
const auth = useAuth();

const showPassword = reactive({
  pw: false,
  pw2: false,
});

const allErrors = computed<string[]>(() => {
  const combined = [...errorsUi.value, ...errorsServer.value];
  return [...new Set(combined)];
});

const isLoading = computed(() => store.validateResetKey.loading || store.validatePassword.loading);

const authenticatorErrors = computed<string[]>(() => {
  const next = allErrors.value.filter((error) => {
    const text = error.toLowerCase();
    return text.includes("code") || text.includes("authenticator") || text.includes("setup");
  });
  return next;
});

const errorsListAuthenticatorCode = computed<string[]>(() => [
  ...(errorsListAuthenticatorPassword.value.length || passwordMismatch.value
    ? ["There is an issue with your password."]
    : []),

  ...authenticatorErrors.value,
]);

const errorsListAuthenticatorPassword = computed<string[]>(() => {
  const next = allErrors.value.filter((error) => {
    const text = error.toLowerCase();

    return text.includes("password");
  });

  return [...new Set(next)];
});

// ENG-20376 frontend only password checks & errors
const validatePassword = () => {
  const nextErrors = [];
  errorsUi.value = [];
  if (store.password.length > 0 && store.password.length < minPasswordLength) {
    nextErrors.push(`Password must be at least ${minPasswordLength} characters`);
  }
  if (
    passwordMismatch.value ||
    (store.password !== store.passwordConfirm && store.passwordConfirm.length > 0)
  ) {
    nextErrors.push("Passwords do not match");
  }
  errorsUi.value = nextErrors;
};

async function doAccountSetup() {
  errorsServer.value = [];
  validatePassword();

  if (!store.password || !store.passwordConfirm) {
    return;
  }

  if (store.password !== store.passwordConfirm) {
    passwordMismatch.value = true;
    return;
  }

  if (errorsListAuthenticatorPassword.value.length) {
    // if UI validation errors, don't try to do the API call
    return;
  }

  passwordMismatch.value = false;

  try {
    const r = await store.setupUser({
      username: props.username,
      setupCode: props.setupCode,
      otpCode: otpCode.value,
      password: {
        password: store.password,
        passwordConfirm: store.passwordConfirm,
      },
      secret: store.mfaSecret.result?.data.secret,
    });
    if (r) {
      await auth.refreshUserInfo();

      await nextTick(() => {
        return router.push({
          name: "tenants",
        });
      });
    }
  } catch (e: any) {
    const setupErrors = Object.values(
      (e.response?.data?.errors as Record<string, string>) ?? {},
    ).flat();
    errorsServer.value = setupErrors ?? [];
    console.error(e, setupErrors);
  }
}

defineExpose({
  otpCode,
  doAccountSetup,
});
</script>
<template>
  <m-layout-stack>
    <m-form
      hide-required-message
      :enter-submit="true"
      :scroll-to-error="!passwordMismatch && errorsListAuthenticatorPassword?.length === 0"
      @submit="doAccountSetup"
    >
      <MBanner
        v-if="errorsListAuthenticatorPassword.length"
        type="danger"
        data-testid="password-mismatch-error-display"
        class="m-mb-md"
        role="alert"
        aria-label="Password errors"
      >
        <ul class="m-pa-none list-style-none" data-testid="ui-error-list">
          <li
            v-for="(errorText, i) in errorsListAuthenticatorPassword"
            :key="`err-${i}`"
            data-testid="error-password"
            class="m-mb-xs"
          >
            {{ errorText }}
          </li>
        </ul>
      </MBanner>
      <m-input
        v-model="store.password"
        outlined
        label="Password"
        autocomplete="new-password"
        :type="showPassword.pw ? 'text' : 'password'"
        color="primary"
        data-testid="password-input"
        placeholder=""
        required
        @change="validatePassword"
        @blur="validatePassword"
      >
        <template #append>
          <m-button
            :icon="showPassword.pw ? 'fa-regular fa-eye-slash' : 'fa-regular fa-eye'"
            color="secondary"
            ghost
            icon-only
            :label="showPassword.pw ? 'Hide password' : 'Show password'"
            data-testid="password-toggle-button"
            @click="showPassword.pw = !showPassword.pw"
          />
        </template>
      </m-input>
      <m-input
        v-model="store.passwordConfirm"
        outlined
        label="Confirm password"
        :type="showPassword.pw2 ? 'text' : 'password'"
        color="primary"
        data-testid="confirm-password-input"
        autocomplete="new-password"
        placeholder=""
        required
        @change="validatePassword"
        @blur="validatePassword"
      >
        <template #append>
          <m-button
            :icon="showPassword.pw2 ? 'fa-regular fa-eye-slash' : 'fa-regular fa-eye'"
            color="secondary"
            ghost
            icon-only
            :label="showPassword.pw2 ? 'Hide password confirmation' : 'Show password confirmation'"
            data-testid="confirm-password-toggle-button"
            @click="showPassword.pw2 = !showPassword.pw2"
          />
        </template>
      </m-input>

      <m-banner type="info" class="m-mb-sm">
        <strong>Secure password tip: </strong> The National Cyber Security Centre advises creating a
        password using
        <a
          href="https://www.ncsc.gov.uk/blog-post/three-random-words-or-thinkrandom-0"
          target="_blank"
          >three random words</a
        >.
      </m-banner>

      <m-layout-stack v-if="store.mfaSecret.result?.data.qrCode">
        <p class="m-my-lg text-center">
          Scan the QR code with your authenticator app (e.g. Google Authenticator or Microsoft
          Authenticator) to pair a new authenticator app:
        </p>
        <div
          style="width: 50%"
          class="m-mx-auto"
          data-testid="qr-code-display"
          v-html="store.mfaSecret.result?.data?.qrCode"
        />
        <p class="m-my-lg text-center">
          Or enter manually: <br />{{ store.mfaSecret.result?.data?.secret }}
        </p>
        <div
          v-if="errorsListAuthenticatorCode.length > 0"
          type="error"
          class="error-text m-mb-sm"
          data-testid="error-authenticator-container"
          role="alert"
          aria-label="Authenticator errors"
        >
          <p
            v-for="errorText in errorsListAuthenticatorCode"
            :key="errorText"
            data-testid="error-authenticator"
          >
            {{ errorText }}
          </p>
        </div>
        <m-input
          v-model="otpCode"
          label="6 digit token from authenticator app"
          required
          mask="999999"
          inputmode="numeric"
          data-testid="otp-code-input"
          autocomplete="off"
          aria-autocomplete="none"
        />
      </m-layout-stack>

      <template #footer>
        <m-button
          type="submit"
          class="float-right login-button w-full"
          data-cy="changePasswordButton"
          :disable="isLoading"
          data-testid="setup-submit"
          @click="doAccountSetup"
        >
          Complete setup
        </m-button>
      </template>
    </m-form>
  </m-layout-stack>
</template>
